import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Check, X, Wand2, Loader2, AlertCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { extractErrorMessage } from '../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONF_META = {
  high: { label: 'Confiance élevée', cls: 'border-emerald-500/60 text-emerald-300' },
  medium: { label: 'Confiance moyenne', cls: 'border-amber-500/60 text-amber-300' },
  low: { label: 'Confiance faible', cls: 'border-orange-500/60 text-orange-300' },
  unknown: { label: 'Inconnue', cls: 'border-zinc-600 text-zinc-400' },
};

const TABS = [
  { key: 'pending', label: 'À valider' },
  { key: 'accepted', label: 'Acceptées' },
  { key: 'rejected', label: 'Rejetées' },
];

const AdminDupes = () => {
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [scope, setScope] = useState('missing');
  const [busy, setBusy] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!isStaff) navigate('/connexion');
  }, [isStaff, navigate]);

  const fetchSuggestions = useCallback(async (t = tab) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dupes/suggestions?status=${t}`);
      setSuggestions(res.data.suggestions || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const fetchJob = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dupes/status`);
      setJob(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);
  useEffect(() => { fetchJob(); }, [fetchJob]);

  // Poll while an analysis is running
  useEffect(() => {
    if (!job?.running) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      const j = await fetchJob();
      if (j && !j.running) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.success('Analyse terminée !');
        fetchSuggestions();
      }
    }, 3000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [job?.running, fetchJob, fetchSuggestions]);

  const startAnalysis = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/dupes/analyze`, { scope });
      toast.success(`Analyse lancée sur ${res.data.total} produits`);
      fetchJob();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Impossible de lancer l'analyse"));
    }
  };

  const accept = async (s, overrides) => {
    setBusy(s.id);
    try {
      await axios.post(`${API_URL}/api/admin/dupes/suggestions/${s.id}/accept`, overrides || {});
      toast.success(`« ${s.product_name} » mis à jour`);
      fetchSuggestions();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally { setBusy(null); }
  };

  const reject = async (s) => {
    setBusy(s.id);
    try {
      await axios.post(`${API_URL}/api/admin/dupes/suggestions/${s.id}/reject`);
      fetchSuggestions();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally { setBusy(null); }
  };

  const acceptAllHigh = async () => {
    const ids = suggestions.filter((s) => s.confidence === 'high').map((s) => s.id);
    if (ids.length === 0) { toast.warning('Aucune suggestion à confiance élevée'); return; }
    setBusy('bulk');
    try {
      const res = await axios.post(`${API_URL}/api/admin/dupes/accept-bulk`, { ids });
      toast.success(`${res.data.accepted} produits mis à jour`);
      fetchSuggestions();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally { setBusy(null); }
  };

  if (!isStaff) return null;

  const progressPct = job?.total ? Math.round(((job.processed || 0) / job.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-dupes-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to="/admin" className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Tableau de bord
          </Link>
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">Inspirations & Notes</p>
          <h1 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Équivalences de parfums (IA)
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            L'IA identifie le parfum de créateur équivalent et les notes olfactives. Vous validez chaque
            suggestion avant qu'elle soit ajoutée à la fin de la description — les clients pourront ensuite
            rechercher par inspiration (ex : « Sauvage ») ou par note (ex : « vanille »).
          </p>
        </div>

        {/* Analyze panel */}
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-5 mb-6" data-testid="dupes-analyze-panel">
          {job?.running ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-4 h-4 text-[color:var(--color-primary)] animate-spin" />
                <p className="text-white text-sm">
                  Analyse en cours… {job.processed || 0} / {job.total} produits
                  {job.errors > 0 && <span className="text-orange-400"> ({job.errors} erreurs)</span>}
                </p>
              </div>
              <div className="h-1.5 bg-white/5 overflow-hidden">
                <div className="h-full bg-[color:var(--color-primary)] transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
              <p className="text-zinc-600 text-xs mt-2">Vous pouvez quitter la page, l'analyse continue en arrière-plan.</p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <select value={scope} onChange={(e) => setScope(e.target.value)}
                data-testid="dupes-scope-select"
                className="bg-black border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none">
                <option value="missing">Produits sans inspiration (recommandé)</option>
                <option value="all">Tout le catalogue (ré-analyse)</option>
              </select>
              <button type="button" onClick={startAnalysis}
                data-testid="dupes-start-analysis"
                className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] transition-colors">
                <Wand2 className="w-4 h-4" /> Lancer l'analyse IA
              </button>
              {job?.finished_at && (
                <p className="text-zinc-600 text-xs">
                  Dernière analyse : {job.processed || 0} produits{job.errors > 0 ? `, ${job.errors} erreurs` : ''}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              data-testid={`dupes-tab-${t.key}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                tab === t.key ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]'
                  : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
              {t.label} ({counts[t.key] || 0})
            </button>
          ))}
          {tab === 'pending' && suggestions.some((s) => s.confidence === 'high') && (
            <button type="button" onClick={acceptAllHigh} disabled={busy === 'bulk'}
              data-testid="dupes-accept-all-high"
              className="ml-auto flex items-center gap-1.5 border border-emerald-500/50 text-emerald-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-emerald-500/10 disabled:opacity-40 transition-colors">
              <Check className="w-3.5 h-3.5" />
              Accepter toutes les « confiance élevée » ({suggestions.filter((s) => s.confidence === 'high').length})
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-zinc-500 text-sm py-12 text-center">Chargement…</p>
        ) : suggestions.length === 0 ? (
          <div className="border border-white/10 bg-[color:var(--color-surface)] p-10 text-center" data-testid="dupes-empty">
            <Sparkles className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">
              {tab === 'pending' ? "Aucune suggestion en attente. Lancez l'analyse IA ci-dessus." : 'Rien ici pour le moment.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((s) => (
              <SuggestionCard key={s.id} suggestion={s} tab={tab}
                busy={busy === s.id} onAccept={accept} onReject={reject} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SuggestionCard = ({ suggestion: s, tab, busy, onAccept, onReject }) => {
  const [inspiredBy, setInspiredBy] = useState(s.inspired_by || '');
  const [inspiredBrand, setInspiredBrand] = useState(s.inspired_by_brand || '');
  const [notesTop, setNotesTop] = useState((s.notes_top || []).join(', '));
  const [notesHeart, setNotesHeart] = useState((s.notes_heart || []).join(', '));
  const [notesBase, setNotesBase] = useState((s.notes_base || []).join(', '));
  const conf = CONF_META[s.confidence] || CONF_META.unknown;
  const toList = (str) => str.split(',').map((v) => v.trim()).filter(Boolean);
  const hasContent = inspiredBy.trim() || notesTop.trim() || notesHeart.trim() || notesBase.trim();

  return (
    <div className="border border-white/10 bg-[color:var(--color-surface)] p-4" data-testid={`dupe-card-${s.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <p className="text-white text-sm font-medium">{s.product_name}</p>
        <p className="text-zinc-500 text-xs uppercase">{s.brand}</p>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${conf.cls}`}
              data-testid={`dupe-confidence-${s.id}`}>
          {conf.label}
        </span>
        {!s.inspired_by && s.confidence === 'unknown' && (
          <span className="flex items-center gap-1 text-zinc-500 text-[11px]">
            <AlertCircle className="w-3 h-3" /> Pas d'équivalence connue — notes uniquement
          </span>
        )}
      </div>

      {tab === 'pending' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-zinc-500 text-[11px] block mb-1">✨ Inspiré de (parfum original)</label>
            <input value={inspiredBy} onChange={(e) => setInspiredBy(e.target.value)}
              placeholder="Ex : Sauvage Elixir"
              data-testid={`dupe-inspired-input-${s.id}`}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[11px] block mb-1">Marque du parfum original</label>
            <input value={inspiredBrand} onChange={(e) => setInspiredBrand(e.target.value)}
              placeholder="Ex : Dior"
              data-testid={`dupe-brand-input-${s.id}`}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[11px] block mb-1">🌿 Notes de tête</label>
            <input value={notesTop} onChange={(e) => setNotesTop(e.target.value)}
              placeholder="bergamote, poivre"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-zinc-500 text-[11px] block mb-1">Cœur</label>
              <input value={notesHeart} onChange={(e) => setNotesHeart(e.target.value)}
                placeholder="lavande"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
            <div>
              <label className="text-zinc-500 text-[11px] block mb-1">Fond</label>
              <input value={notesBase} onChange={(e) => setNotesBase(e.target.value)}
                placeholder="ambre, vanille"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-zinc-400 mb-2 space-y-0.5">
          {s.inspired_by && (
            <p>✨ Inspiré de : <span className="text-white">{s.inspired_by}{s.inspired_by_brand ? ` — ${s.inspired_by_brand}` : ''}</span></p>
          )}
          {(s.notes_top?.length > 0 || s.notes_heart?.length > 0 || s.notes_base?.length > 0) && (
            <p className="text-xs">
              🌿 {[s.notes_top?.length && `Tête : ${s.notes_top.join(', ')}`,
                   s.notes_heart?.length && `Cœur : ${s.notes_heart.join(', ')}`,
                   s.notes_base?.length && `Fond : ${s.notes_base.join(', ')}`].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      )}

      {tab === 'pending' && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={busy || !hasContent}
            onClick={() => onAccept(s, {
              inspired_by: inspiredBy.trim() || null,
              inspired_by_brand: inspiredBrand.trim() || null,
              notes_top: toList(notesTop),
              notes_heart: toList(notesHeart),
              notes_base: toList(notesBase),
            })}
            data-testid={`dupe-accept-${s.id}`}
            className="flex items-center gap-1.5 bg-emerald-500 text-black px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-emerald-400 disabled:opacity-40 transition-colors">
            <Check className="w-3.5 h-3.5" /> Accepter → Ajouter à la description
          </button>
          <button type="button" disabled={busy}
            onClick={() => onReject(s)}
            data-testid={`dupe-reject-${s.id}`}
            className="flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-red-500/10 disabled:opacity-40 transition-colors">
            <X className="w-3.5 h-3.5" /> Rejeter
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDupes;
